<template>
  <div class="template-shell" :class="{ 'reduced-motion': prefersReducedMotion }">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main v-else class="museum-room">
      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune œuvre dans cette galerie</div>

      <template v-else>
        <header class="museum-header">
          <h1>{{ exhibitName }}</h1>
          <p v-if="userName" class="signature">Une exposition par {{ userName }}</p>
        </header>

        <div class="wall" @click.self="closeLit">
          <article
            v-for="(resource, index) in resources"
            :key="resource.id || resource.name || index"
            :ref="(el) => registerArtworkEl(el, index)"
            :data-artwork-index="index"
            class="artwork"
            :class="[
              `size-${layoutFor(index).size}`,
              `align-${layoutFor(index).align}`,
              {
                lit: litIndex === index,
                'in-view': isTouch && inViewIndex === index,
                'is-audio': getMediaType(resource.name) === 'audio',
                playing: playingIndex === index,
              },
            ]"
            :style="{ marginTop: index === 0 ? 0 : `${40 + layoutFor(index).shift}px` }"
            role="button"
            tabindex="0"
            :aria-label="artworkLabel(resource, index)"
            @click.stop="onArtworkClick(index)"
            @keydown.enter.prevent="onArtworkClick(index)"
            @keydown.space.prevent="onArtworkClick(index)"
          >
            <div class="frame" :class="`ratio-${layoutFor(index).ratio}`">
              <span v-if="getMediaType(resource.name) === 'audio'" class="plaque">Salle d'écoute</span>
              <ResourceMedia
                :resource="resource"
                :url="resourceUrls[index]"
                context="thumb"
                fit="cover"
                :active="isArtworkActive(index)"
                @media-play="onMediaPlay(index)"
                @media-pause="onMediaPause(index)"
              />
            </div>

            <transition name="cartel-fade">
              <div v-if="litIndex === index" class="cartel">
                <h2>{{ cartelTitle(resource) }}</h2>
                <p v-if="cartelDescription(resource)">{{ cartelDescription(resource) }}</p>
              </div>
            </transition>
          </article>
        </div>

        <div v-if="!isTouch" ref="torchRef" class="torch-overlay" aria-hidden="true"></div>
      </template>
    </main>

    <ResourceLightbox :resources="resources" :urls="resourceUrls" v-model:selectedIndex="selectedIndex" />
  </div>
</template>

<script setup>
import { defineProps, onBeforeUnmount, onMounted, ref } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';
import { getMediaType } from '../../utils/mediaType';
import { cleanText } from '../../utils/galleryText';
import ResourceMedia from '../../components/gallery/ResourceMedia.vue';
import ResourceLightbox from '../../components/gallery/ResourceLightbox.vue';

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  homeImage,
  homePage,
  id,
  loading,
  resourceUrls,
  resources,
  userName,
} = useGalleryTemplate(props);

/* ------------------------------- lit / lightbox state ------------------------------- */

const litIndex = ref(null);
const selectedIndex = ref(null);

const onArtworkClick = (index) => {
  if (litIndex.value === index) {
    selectedIndex.value = index;
    return;
  }
  litIndex.value = index;
};

const closeLit = () => {
  litIndex.value = null;
};

const cartelTitle = (resource) => cleanText(resource?.title) || cleanText(resource?.name) || 'Sans titre';
const cartelDescription = (resource) => cleanText(resource?.description);
const artworkLabel = (resource, index) => `${cartelTitle(resource)} — œuvre ${index + 1} sur ${resources.value.length}`;

/* ------------------------------ media playback state -------------------------------- */

// Une œuvre est « éclairée » (donc sa vidéo peut jouer) quand elle est
// spotlightée au clic, ou — sur mobile — quand elle est centrée dans le viewport.
const isArtworkActive = (index) =>
  litIndex.value === index || (isTouch.value && inViewIndex.value === index);

// Index de la ressource en cours de lecture (relayé par ResourceMedia depuis
// les événements natifs play/pause) — pilote le halo pulsant pendant la lecture.
const playingIndex = ref(null);
const onMediaPlay = (index) => {
  playingIndex.value = index;
};
const onMediaPause = (index) => {
  if (playingIndex.value === index) playingIndex.value = null;
};

/* ------------------------------------- layout --------------------------------------- */

const LAYOUT_PRESETS = [
  { size: 'lg', align: 'start', shift: 10, ratio: 'portrait' },
  { size: 'md', align: 'end', shift: 90, ratio: 'landscape' },
  { size: 'sm', align: 'start', shift: 150, ratio: 'square' },
  { size: 'md', align: 'end', shift: 30, ratio: 'portrait' },
  { size: 'lg', align: 'end', shift: 110, ratio: 'landscape' },
  { size: 'sm', align: 'start', shift: 60, ratio: 'square' },
];

const layoutFor = (index) => LAYOUT_PRESETS[index % LAYOUT_PRESETS.length];

/* --------------------------------- desktop vs mobile --------------------------------- */

const isTouch = ref(true);
const prefersReducedMotion = ref(false);

if (typeof window !== 'undefined' && window.matchMedia) {
  isTouch.value = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ------------------------------ desktop torch overlay -------------------------------- */

const torchRef = ref(null);
let rafId = null;
let pendingX = null;
let pendingY = null;

const flushTorchPosition = () => {
  rafId = null;
  if (torchRef.value && pendingX !== null) {
    torchRef.value.style.setProperty('--tx', `${pendingX}px`);
    torchRef.value.style.setProperty('--ty', `${pendingY}px`);
  }
};

const onMouseMove = (event) => {
  pendingX = event.clientX;
  pendingY = event.clientY;
  if (rafId === null) {
    rafId = requestAnimationFrame(flushTorchPosition);
  }
};

/* --------------------------- mobile: centered artwork via IO -------------------------- */

const inViewIndex = ref(null);
const artworkEls = new Map();
const visibilityRatios = new Map();
let intersectionObserver = null;

const recomputeInView = () => {
  let bestIndex = null;
  let bestRatio = 0;
  visibilityRatios.forEach((ratio, index) => {
    if (ratio >= 0.6 && ratio > bestRatio) {
      bestRatio = ratio;
      bestIndex = index;
    }
  });
  inViewIndex.value = bestIndex;
};

const registerArtworkEl = (el, index) => {
  if (!isTouch.value) return;
  const previous = artworkEls.get(index);
  if (previous && previous !== el && intersectionObserver) {
    intersectionObserver.unobserve(previous);
    visibilityRatios.delete(index);
  }
  if (el) {
    artworkEls.set(index, el);
    if (intersectionObserver) intersectionObserver.observe(el);
  } else {
    artworkEls.delete(index);
    visibilityRatios.delete(index);
    recomputeInView();
  }
};

onMounted(() => {
  if (!isTouch.value) {
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    return;
  }
  if (typeof IntersectionObserver === 'undefined') return;
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const index = Number(entry.target.dataset.artworkIndex);
        visibilityRatios.set(index, entry.intersectionRatio);
      });
      recomputeInView();
    },
    { threshold: [0, 0.6, 1] }
  );
  artworkEls.forEach((el) => intersectionObserver.observe(el));
});

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onMouseMove);
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }
});
</script>

<style scoped>
.template-shell {
  min-height: 100vh;
  background: #0a0a0a;
  color: #eee;
}

.museum-room {
  position: relative;
  min-height: 100vh;
  padding-bottom: 12vh;
}

.state-panel {
  display: grid;
  place-items: center;
  min-height: 100vh;
  color: #b8b0a0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 20px;
  letter-spacing: 0.02em;
}

.museum-header {
  position: relative;
  z-index: 6;
  max-width: 900px;
  margin: 0 auto;
  padding: 12vh 8vw 8vh;
  text-align: center;
}

.museum-header h1 {
  margin: 0 0 14px;
  font-family: Georgia, 'Times New Roman', serif;
  font-weight: 400;
  font-size: clamp(38px, 5.2vw, 68px);
  letter-spacing: 0.03em;
  color: #d8cfae;
  text-shadow: 0 0 22px rgba(201, 162, 39, 0.18);
}

.signature {
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-size: 16px;
  letter-spacing: 0.06em;
  color: #6f6a5c;
}

.wall {
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 1100px;
  margin: 0 auto;
  padding: 4vh 6vw 10vh;
}

.artwork {
  position: relative;
  cursor: pointer;
  outline: none;
}

.artwork.align-start {
  align-self: flex-start;
}

.artwork.align-end {
  align-self: flex-end;
}

.artwork.size-sm {
  width: min(50vw, 300px);
}

.artwork.size-md {
  width: min(58vw, 420px);
}

.artwork.size-lg {
  width: min(68vw, 560px);
}

.artwork:focus-visible .frame {
  box-shadow: 0 0 0 2px rgba(201, 162, 39, 0.7);
}

.frame {
  position: relative;
  overflow: hidden;
  background: #141210;
  border: 1px solid rgba(201, 162, 39, 0.16);
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.6);
  transition: transform 480ms ease, box-shadow 480ms ease;
}

.frame.ratio-portrait {
  aspect-ratio: 3 / 4;
}

.frame.ratio-landscape {
  aspect-ratio: 4 / 3;
}

.frame.ratio-square {
  aspect-ratio: 1 / 1;
}

.frame :deep(.resource-media) {
  filter: brightness(0.24) contrast(0.9) saturate(0.65);
  transition: filter 560ms ease;
}

.plaque {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  padding: 3px 9px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #d8cfae;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(201, 162, 39, 0.3);
  pointer-events: none;
}

/* mobile: artwork centered in viewport gets a soft ambient halo */
.artwork.in-view .frame {
  box-shadow: 0 0 60px 8px rgba(255, 214, 130, 0.16), 0 18px 46px rgba(0, 0, 0, 0.6);
}

.artwork.in-view .frame :deep(.resource-media) {
  filter: brightness(0.55) contrast(0.95) saturate(0.85);
}

/* clicked / active artwork: fixed spotlight, independent of the torch */
.artwork.lit {
  z-index: 5;
}

.artwork.lit .frame {
  transform: scale(1.035);
  box-shadow: 0 0 70px 10px rgba(255, 214, 130, 0.28), 0 22px 54px rgba(0, 0, 0, 0.7);
  border-color: rgba(201, 162, 39, 0.55);
}

.artwork.lit .frame :deep(.resource-media) {
  filter: brightness(1) contrast(1) saturate(1);
}

/* « salle d'écoute » : halo pulsant pendant la lecture audio uniquement */
.artwork.is-audio.playing .frame {
  animation: pulse-halo 2.6s ease-in-out infinite;
}

.reduced-motion .artwork.is-audio.playing .frame {
  animation: none;
}

@keyframes pulse-halo {
  0%,
  100% {
    box-shadow: 0 0 70px 10px rgba(255, 214, 130, 0.28), 0 22px 54px rgba(0, 0, 0, 0.7);
  }
  50% {
    box-shadow: 0 0 96px 18px rgba(255, 214, 130, 0.42), 0 22px 54px rgba(0, 0, 0, 0.7);
  }
}

.cartel {
  max-width: 320px;
  margin-top: 14px;
  padding: 12px 16px;
  font-family: Georgia, 'Times New Roman', serif;
  background: rgba(10, 9, 7, 0.72);
  border-left: 2px solid rgba(201, 162, 39, 0.55);
}

.artwork.align-end .cartel {
  margin-left: auto;
  text-align: right;
  border-left: none;
  border-right: 2px solid rgba(201, 162, 39, 0.55);
}

.cartel h2 {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 400;
  letter-spacing: 0.03em;
  color: #d8cfae;
}

.cartel p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #9c9584;
}

.cartel-fade-enter-active,
.cartel-fade-leave-active {
  transition: opacity 320ms ease, transform 320ms ease;
}

.cartel-fade-enter-from,
.cartel-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* desktop-only flashlight overlay: a big dark layer with a transparent hole
   following the mouse via CSS custom properties (no per-pixel Vue reactivity). */
.torch-overlay {
  position: fixed;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  background: radial-gradient(
    circle 260px at var(--tx, 50vw) var(--ty, 30vh),
    transparent 0%,
    rgba(8, 8, 8, 0.5) 42%,
    rgba(6, 6, 6, 0.92) 78%,
    rgba(5, 5, 5, 0.97) 100%
  );
}

@media (max-width: 860px) {
  .museum-header {
    padding: 8vh 7vw 6vh;
  }

  .wall {
    padding: 2vh 6vw 8vh;
    gap: 0;
  }

  .artwork.size-sm,
  .artwork.size-md,
  .artwork.size-lg {
    width: 100%;
  }

  .artwork.align-start,
  .artwork.align-end {
    align-self: stretch;
  }

  .cartel {
    max-width: none;
  }

  .artwork.align-end .cartel {
    margin-left: 0;
    text-align: left;
    border-left: 2px solid rgba(201, 162, 39, 0.55);
    border-right: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .frame,
  .frame :deep(.resource-media) {
    transition: none;
  }

  .artwork.is-audio.playing .frame {
    animation: none;
  }
}
</style>
